import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api'
import { getGoogleMapsApiKey } from 'utils/constants'
import GeofenceMarker from './GeofenceMarker'
import PlacesAutocompleteSearch from './PlacesAutocompleteSearch'

const mapContainerStyle = { width: '100%', height: '400px' }
const defaultCenter = { lat: 40.6940558, lng: -73.9839499 }

const MapInner = props => {
  const {
    geofence,
    onPlaceSelected,
    renderMarker,
    zoom,
    editMode,
    preview = false,
    validateCoords,
    onSetMarker,
    recenter,
    onSetRecenter,
    nameEntered,
  } = props
  const { latitude, longitude } = geofence
  const [currentCenter, setCurrentCenter] = useState(defaultCenter)
  const mapRef = useRef(null)

  const onLoad = useCallback(map => {
    mapRef.current = map
  }, [])

  const onRecenterMap = useCallback(() => {
    if (!mapRef.current) return
    const center = mapRef.current.getCenter()
    if (center) {
      const lat = center.lat()
      const lng = center.lng()
      setCurrentCenter({ lat, lng })
    }
  }, [])

  const onSetMarkerAndPopulateNameFields = useCallback(
    (coords, isNotPOI) => {
      let placeName = ''
      const { lat, lng } = coords

      const poiEl = document.getElementsByClassName('poi-info-window')[0]
      const placeTitleEl = poiEl ? poiEl.getElementsByClassName('title full-width')[0] : null

      const isPlace = placeTitleEl ? placeTitleEl.parentNode.style.display !== 'none' : false
      const placeTitle = placeTitleEl ? placeTitleEl.innerText : ''

      const transitEl = poiEl ? poiEl.getElementsByClassName('transit-container')[0] : null
      const isTransit = transitEl ? transitEl.style.display !== 'none' : false

      const transitTitle = transitEl ? transitEl.getElementsByTagName('span')[1]?.innerText : ''

      if (isPlace) placeName = placeTitle
      else if (isTransit) placeName = transitTitle
      if (isNotPOI) placeName = ''

      const placeSearchInput = document.getElementsByClassName('places-search')[0]
      if (placeSearchInput) placeSearchInput.value = placeName

      onSetMarker({ lat, lng }, placeName, false, nameEntered)
      validateCoords({ lat, lng })
    },
    [onSetMarker, validateCoords, nameEntered],
  )

  const onMapClick = useCallback(
    e => {
      onRecenterMap()
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      const isNotPOI = !e.placeId

      const infoWindowCloseButton = document.getElementsByClassName('gm-ui-hover-effect')[0]
      if (isNotPOI && infoWindowCloseButton) infoWindowCloseButton.click()

      setTimeout(() => onSetMarkerAndPopulateNameFields({ lat, lng }, isNotPOI), 50)
    },
    [onRecenterMap, onSetMarkerAndPopulateNameFields],
  )

  const hasLat = !!latitude || latitude === 0
  const hasLng = !!longitude || longitude === 0
  const center = {
    lat: recenter && hasLat ? Number(latitude) : currentCenter.lat,
    lng: recenter && hasLng ? Number(longitude) : currentCenter.lng,
  }
  const mapZoom = editMode || preview ? 15 : zoom

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      onLoad={onLoad}
      onDragEnd={onRecenterMap}
      onDragStart={() => onSetRecenter(false)}
      onClick={preview ? undefined : onMapClick}
      zoom={mapZoom}
      defaultZoom={8}
      center={center}
      defaultCenter={{ lat: latitude ? Number(latitude) : defaultCenter.lat, lng: longitude ? Number(longitude) : defaultCenter.lng }}
    >
      {!preview && <PlacesAutocompleteSearch className='places-search' onPlaceSelected={onPlaceSelected} />}
      {renderMarker(onRecenterMap)}
    </GoogleMap>
  )
}

MapInner.propTypes = {
  geofence: PropTypes.shape({
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onPlaceSelected: PropTypes.func,
  renderMarker: PropTypes.func,
  zoom: PropTypes.number,
  editMode: PropTypes.bool,
  preview: PropTypes.bool,
  validateCoords: PropTypes.func,
  onSetMarker: PropTypes.func,
  recenter: PropTypes.bool,
  onSetRecenter: PropTypes.func,
  nameEntered: PropTypes.bool,
}

class Map extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isNewMarkerDown: false,
      zoom: 8,
      recenter: true,
    }
  }

  onSetRecenter = bool => {
    this.setState({ recenter: bool })
  }

  onSetMarker = (place, placeName, recenterMap = true, nameEntered) => {
    const { setGeofence, geofence, nameRef } = this.props
    const { lat, lng } = place
    this.setState({ recenter: recenterMap }, () => {
      this.setState({ isNewMarkerDown: true })
    })

    setGeofence({
      ...geofence,
      latitude: lat,
      longitude: lng,
      name: nameEntered ? geofence.name : placeName,
    })
    if (nameRef?.current) nameRef.current.focus()
  }

  renderMarker = onRecenterMapCB => {
    const { geofence, editMode, preview, fieldsEntered } = this.props
    const { isNewMarkerDown } = this.state
    const { latitude, longitude, radius } = geofence
    const markerPosition = {
      lat: Number(latitude),
      lng: Number(longitude),
    }

    return isNewMarkerDown || editMode || preview || fieldsEntered ? (
      <GeofenceMarker
        draggable={!preview}
        preview={preview}
        onMarkerDragEnd={e => {
          onRecenterMapCB()
          this.onMarkerDragEnd(e)
        }}
        markerPosition={markerPosition}
        radius={radius}
      />
    ) : null
  }

  onMarkerDragEnd = event => {
    const { setGeofence, geofence, validateCoords, nameEntered } = this.props
    const placeSearchInput = document.getElementsByClassName('places-search')[0]
    const newLat = event.latLng.lat()
    const newLng = event.latLng.lng()
    if (placeSearchInput) placeSearchInput.value = ''

    setGeofence({
      ...geofence,
      name: nameEntered ? geofence.name : '',
      latitude: newLat,
      longitude: newLng,
    })
    validateCoords({ lat: newLat, lng: newLng })
  }

  onPlaceSelected = place => {
    const { validateCoords, nameEntered } = this.props
    const { geometry } = place
    const location = geometry ? geometry.location : {}
    const hasLocation = location && (typeof location.lat === 'function' ? true : Object.keys(location).length !== 0)
    const searchEl = document.getElementsByClassName('places-search')[0]
    const valueInSearch = searchEl ? searchEl.value : ''
    const placeName = valueInSearch.split(',')[0]
    if (hasLocation && location) {
      const lat = typeof location.lat === 'function' ? location.lat() : location.lat
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng
      this.onSetMarker({ lat, lng }, placeName, true, nameEntered)
      this.setState({ zoom: 17 })
      validateCoords({ lat, lng })
    }
  }

  render() {
    const { zoom, recenter } = this.state

    return (
      <div className='map-wrapper'>
        <MapWithLoader
          {...this.props}
          renderMarker={this.renderMarker}
          onPlaceSelected={this.onPlaceSelected}
          onSetMarker={this.onSetMarker}
          recenter={recenter}
          onSetRecenter={this.onSetRecenter}
          zoom={zoom}
        />
      </div>
    )
  }
}

function MapWithLoader(props) {
  const apiKey = getGoogleMapsApiKey()
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-geofence',
    googleMapsApiKey: apiKey || '',
    libraries: ['places', 'geometry', 'drawing'],
  })

  if (loadError) {
    return <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Error loading map</div>
  }
  if (!isLoaded) {
    return <div style={{ height: '400px' }} />
  }
  return <MapInner {...props} />
}

Map.propTypes = {
  geofence: PropTypes.shape({
    name: PropTypes.string,
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setGeofence: PropTypes.func,
  validateCoords: PropTypes.func,
  editMode: PropTypes.bool,
  preview: PropTypes.bool,
  fieldsEntered: PropTypes.bool,
  nameEntered: PropTypes.bool,
  nameRef: PropTypes.shape({
    current: PropTypes.object,
  }),
}

export default Map
